import url from '../constants/urlList';
import {
    isDevAuthStubEnabled,
    DEV_AUTHSTUB_CREDENTIALS,
    DEV_AUTHSTUB_OTP_CODE,
    DEV_AUTHSTUB_STUB_PHONE
} from '../constants/devAuthStub';

/*
 * Dev auth stub service — see src/constants/devAuthStub.js for the safety
 * contract (build-time env gate + prod-API hard refusal).
 *
 * Design notes:
 *   - Sessions are REAL dev-API sessions (POST /auth/signin with the baked
 *     dev creds), so downstream screens see production response shapes and
 *     live data — nothing else is mocked.
 *   - Only the two OTP endpoints are intercepted, because they'd otherwise
 *     fire real SMS. The verify response is fulfilled by the same real
 *     signin, reshaped to what PhoneAuthSection expects
 *     ({ user: { accessToken, id, phoneNumber, isProfileComplete } }).
 *   - The fetch wrapper wraps whatever fetch is current (store.js installs
 *     its auth-header wrapper at module load, before App mounts), so the
 *     intercept sees requests first and unknown URLs pass straight through.
 */

const signinWithDevCredentials = async (fetchImpl) => {
    const { email, password } = DEV_AUTHSTUB_CREDENTIALS;

    if (!email || !password) {
        // eslint-disable-next-line no-console
        console.warn('[dev-auth-stub] enabled but REACT_APP_QLEARN_DEV_AUTHSTUB_EMAIL/PASSWORD are unset — skipping');
        return null;
    }

    const response = await fetchImpl(url.login, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
        // eslint-disable-next-line no-console
        console.warn(`[dev-auth-stub] dev signin failed (${response.status}) — screens stay unauthenticated`);
        return null;
    }

    const data = await response.json();
    // The dev /auth/signin endpoint returns the user fields (id, email,
    // accessToken, …) at the TOP level, whereas some environments nest them
    // under `.user`. Accept both: prefer the nested shape, else treat a
    // top-level payload carrying an accessToken as the user itself. Without
    // the top-level branch the stub seeds no session on the dev API (#490).
    return data?.user || (data?.accessToken ? data : null);
};

// Mirrors what login.jsx persists on a successful email signin, so every
// downstream reader (store.js fetch wrapper, menu, subscriptions) sees a
// session indistinguishable from a hand-typed login.
const seedSession = (user) => {
    window.localStorage.setItem('userdetails', JSON.stringify({
        name: user.displayName,
        email: user.email
    }));
    window.localStorage.setItem('token', user.accessToken);
    window.localStorage.setItem('userId', user.id);

    if (user.primaryRole) {
        window.localStorage.setItem('role', user.primaryRole);
    }
};

const jsonResponse = (body, status = 200) => new Response(JSON.stringify(body), {
    status,
    headers: { 'Content-Type': 'application/json' }
});

const installOtpIntercept = () => {
    const passthroughFetch = window.fetch;

    window.fetch = function devAuthStubFetch(input, options) {
        const requestUrl = typeof input === 'string' ? input : input?.url || '';

        if (requestUrl === url.sendOTP) {
            // No real SMS from capture runs — answer locally.
            return Promise.resolve(jsonResponse({ success: true, isNewUser: false }));
        }

        if (requestUrl === url.verifyOTP) {
            let submittedCode = '';
            try {
                submittedCode = JSON.parse(options?.body || '{}')?.otp || '';
            } catch {
                submittedCode = '';
            }

            if (submittedCode !== DEV_AUTHSTUB_OTP_CODE) {
                // Wrong code exercises the real error UI.
                return Promise.resolve(jsonResponse({ error: 'Invalid OTP' }, 400));
            }

            return signinWithDevCredentials(passthroughFetch).then((user) => {
                if (!user) {
                    return jsonResponse({ error: 'Dev auth stub signin failed' }, 500);
                }

                return jsonResponse({
                    user: {
                        ...user,
                        phoneNumber: user.phoneNumber || DEV_AUTHSTUB_STUB_PHONE,
                        isProfileComplete: true
                    }
                });
            });
        }

        return passthroughFetch.apply(this, arguments);
    };
};

const hasSession = () => {
    const token = window.localStorage.getItem('token');
    return Boolean(token && token !== 'undefined');
};

export const installDevAuthStub = async () => {
    if (!isDevAuthStubEnabled()) {
        return false;
    }

    // eslint-disable-next-line no-console
    console.warn('[dev-auth-stub] ACTIVE — dev-API auto-signin + OTP intercept (never ships in prod builds)');

    installOtpIntercept();

    if (!hasSession()) {
        const user = await signinWithDevCredentials(window.fetch);

        if (user) {
            seedSession(user);

            // Land captures on a real screen instead of the login card.
            const { pathname } = window.location;
            if (pathname === '/' || pathname === '/login') {
                window.location.replace('/create-quiz');
            }
        }
    }

    return true;
};
